/**
 * CategoryReportSection - Displays 6 category report cards with lock/unlock status.
 * Props: billingStatus, diagnosisId
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import billingService from '../../services/billingService';

const REPORT_TYPES = [
  { key: 'work', ja: '仕事・キャリア', en: 'Work & Career' },
  { key: 'romance', ja: '恋愛・パートナーシップ', en: 'Romance & Partnership' },
  { key: 'relationships', ja: '人間関係', en: 'Relationships' },
  { key: 'stress', ja: 'ストレス・メンタルケア', en: 'Stress & Mental Care' },
  { key: 'growth', ja: '自己成長', en: 'Self Growth' },
  { key: 'compatibility', ja: '相性分析', en: 'Compatibility Analysis' },
];

const Container = styled.div`
  background: ${(p) => p.theme.colors.bgTertiary};
  border-radius: ${(p) => p.theme.borderRadius.lg};
  border: 1px solid ${(p) => p.theme.colors.border};
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0;
`;

const SectionDesc = styled.p`
  font-size: 0.82rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 24px;

  @media (min-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ReportCard = styled.button`
  position: relative;
  padding: 16px;
  border-radius: ${(p) => p.theme.borderRadius.md};
  border: 1px solid ${(p) =>
    p.$hasAccess ? 'rgba(99, 102, 241, 0.3)' : p.theme.colors.border};
  background: ${(p) =>
    p.$hasAccess ? 'rgba(99, 102, 241, 0.05)' : p.theme.colors.bgPrimary};
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    border-color: ${(p) => p.theme.colors.primary};
    background: ${(p) =>
      p.$hasAccess ? 'rgba(99, 102, 241, 0.1)' : p.theme.colors.bgHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ReportName = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 8px 0;
`;

const AccessBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.primary};
`;

const PriceBadge = styled.span`
  font-size: 0.72rem;
  color: ${(p) => p.theme.colors.textMuted};
`;

const ProCTA = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.border};
  padding-top: 20px;
`;

const ProTitle = styled.h4`
  font-size: 0.88rem;
  font-weight: 700;
  color: ${(p) => p.theme.colors.text};
  margin: 0 0 8px 0;
`;

const ProDesc = styled.p`
  font-size: 0.78rem;
  color: ${(p) => p.theme.colors.textMuted};
  margin: 0 0 16px 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const PrimaryBtn = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: ${(p) => p.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${(p) => p.theme.borderRadius.md};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
`;

function CategoryReportSection({ billingStatus }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loadingReport, setLoadingReport] = useState(null);

  const isPro = billingStatus?.plan === 'pro';
  const purchasedReports = billingStatus?.purchased_reports ?? [];

  const handleReportClick = async (reportKey) => {
    const hasAccess = isPro || purchasedReports.includes(reportKey);

    if (hasAccess) {
      navigate(`/report/${reportKey}`);
      return;
    }

    setLoadingReport(reportKey);
    try {
      const { checkout_url } = await billingService.createReportCheckout(reportKey);
      window.location.href = checkout_url;
    } catch (err) {
      alert(err.message || 'Failed to create checkout');
    } finally {
      setLoadingReport(null);
    }
  };

  const handleProCheckout = async (planType) => {
    try {
      const { checkout_url } = await billingService.createIndividualCheckout(planType);
      window.location.href = checkout_url;
    } catch (err) {
      alert(err.message || 'Failed to create checkout');
    }
  };

  return (
    <Container>
      <SectionHeader>
        <SectionTitle>{t('あなたの構造を深く知る', 'Discover Your Inner Structure')}</SectionTitle>
      </SectionHeader>
      <SectionDesc>
        {t(
          '6つのカテゴリから、あなたの内面構造がどのように現れるかを詳しく分析します。',
          'Get detailed analysis of how your inner structure manifests across 6 categories.'
        )}
      </SectionDesc>

      <Grid>
        {REPORT_TYPES.map((report) => {
          const hasAccess = isPro || purchasedReports.includes(report.key);
          const isLoading = loadingReport === report.key;
          return (
            <ReportCard
              key={report.key}
              $hasAccess={hasAccess}
              onClick={() => handleReportClick(report.key)}
              disabled={isLoading}
            >
              <ReportName>{t(report.ja, report.en)}</ReportName>
              {hasAccess ? (
                <AccessBadge>{t('閲覧可能', 'Available')}</AccessBadge>
              ) : (
                <PriceBadge>{isLoading ? '...' : '¥300'}</PriceBadge>
              )}
            </ReportCard>
          );
        })}
      </Grid>

      {!isPro && (
        <ProCTA>
          <ProTitle>{t('Proプランで全レポート見放題', 'Unlimited Reports with Pro')}</ProTitle>
          <ProDesc>
            {t(
              '全6カテゴリのレポート + AIチャット無制限 + 再診断',
              'All 6 category reports + Unlimited AI Chat + Re-diagnosis'
            )}
          </ProDesc>
          <ButtonGroup>
            <PrimaryBtn onClick={() => handleProCheckout('monthly')}>
              {t('月額 ¥980 で始める', '¥980/month')}
            </PrimaryBtn>
          </ButtonGroup>
        </ProCTA>
      )}
    </Container>
  );
}

export default CategoryReportSection;
